import { useState } from 'react'
import { Gavel, Scale } from 'lucide-react'
import type { PassDecision } from '@concours/shared'
import { Badge, Button, SegmentedPills } from '@concours/ui'
import { DECISION_VARIANT, fmt, type DisciplineColumn, type EntryView } from '../../lib/results'

type Filter = 'all' | 'passed' | 'supplementary' | 'pending' | 'failed' | 'out'
const MATCH: Record<Filter, (d: PassDecision) => boolean> = {
  all: () => true,
  passed: (d) => d === 'passed',
  supplementary: (d) => d === 'supplementary',
  pending: (d) => d === 'pending',
  failed: (d) => d === 'failed',
  out: (d) => ['eliminated', 'absent', 'incomplete'].includes(d),
}

/**
 * Tableau classé : rang (et rang de catégorie), notes par discipline, note
 * d'étape, décision, ex æquo, motifs ; « Trancher » sur les lignes en attente.
 */
export function RankingTable({
  entries,
  disciplines,
  byCategory,
  onResolve,
}: {
  entries: EntryView[]
  disciplines: DisciplineColumn[]
  byCategory: boolean
  onResolve?: (entry: EntryView) => void
}) {
  const [filter, setFilter] = useState<Filter>(
    entries.some((e) => e.decision === 'pending') ? 'pending' : 'all'
  )
  const count = (f: Filter) => entries.filter((e) => MATCH[f](e.decision)).length
  const rows = entries.filter((e) => MATCH[filter](e.decision))
  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <div className="results-filters">
        <SegmentedPills
          label="Filtrer le classement"
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          options={[
            { value: 'all', label: `Tous · ${entries.length}` },
            { value: 'passed', label: `Admis · ${count('passed')}` },
            { value: 'supplementary', label: `Liste compl. · ${count('supplementary')}` },
            { value: 'pending', label: `À trancher · ${count('pending')}` },
            { value: 'failed', label: `Non admis · ${count('failed')}` },
            { value: 'out', label: `Éliminés, absents · ${count('out')}` },
          ]}
        />
      </div>
      <div className="tbl-wrap">
        <table className="tbl results-table">
          <caption className="sr-only">Classement</caption>
          <thead>
            <tr>
              <th scope="col" className="num">
                Rang
              </th>
              {byCategory && (
                <th scope="col" className="num">
                  Rang cat.
                </th>
              )}
              <th scope="col">Candidat</th>
              {byCategory && <th scope="col">Catégorie</th>}
              {disciplines.map((d) => (
                <th key={d.code} scope="col" className="num" title={d.label}>
                  {d.code}
                  <span className="muted"> ×{fmt(d.coefficient)}</span>
                </th>
              ))}
              <th scope="col" className="num">
                Note
              </th>
              <th scope="col">Décision</th>
              <th scope="col">Motifs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} data-decision={e.decision}>
                <td className="num">
                  {e.rank ?? '—'}
                  {e.tie && (
                    <span className="tie-flag" title="Ex æquo">
                      <Scale aria-label="Ex æquo" size={14} />
                    </span>
                  )}
                </td>
                {byCategory && <td className="num">{e.categoryRank ?? '—'}</td>}
                <th scope="row">
                  <span className="stack" style={{ gap: 0 }}>
                    <span>{e.name ?? e.label}</span>
                    {e.name && <span className="mono muted">{e.applicationNumber}</span>}
                  </span>
                </th>
                {byCategory && <td>{e.categoryLabel ?? '—'}</td>}
                {disciplines.map((d) => {
                  const x = e.details.find((y) => y.code === d.code)
                  return (
                    <td key={d.code} className="num" title={x?.note ?? undefined}>
                      {x?.score !== null && x?.score !== undefined
                        ? fmt(x.score)
                        : (x?.note ?? '—')}
                    </td>
                  )
                })}
                <td className="num">
                  <strong>{fmt(e.score)}</strong>
                </td>
                <td>
                  <span className="stack" style={{ gap: 4, alignItems: 'flex-start' }}>
                    <Badge variant={DECISION_VARIANT[e.decision]}>{e.decisionLabel}</Badge>
                    {e.manual && (
                      <span className="muted results-manual" title={e.manual.reason}>
                        <Gavel aria-hidden="true" size={12} /> {e.manual.by}
                      </span>
                    )}
                    {e.decision === 'pending' && onResolve && (
                      <Button size="sm" variant="primary" onClick={() => onResolve(e)}>
                        Trancher
                      </Button>
                    )}
                  </span>
                </td>
                <td className="results-reasons">
                  {[...e.reasons, ...(e.manual ? [`Décision : ${e.manual.reason}`] : [])].join(
                    ' '
                  ) || '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7 + disciplines.length} className="muted">
                  Aucune ligne.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
